import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Row,
  Modal,
  Form,
  Input,
  message,
} from 'antd';
import { ArrowLeftOutlined, StopOutlined } from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import { getContenTypePorTipoDePadron } from 'api/catalogos';
import { patchInfraccion } from 'api/recaudacion/infracciones';
import { getRecibos } from 'api/recaudacion/recibos';
import Uploader from 'components/Uploader';
import { getConceptosDeInfracciones, deleteConceptoDeInfraccion } from 'api/recaudacion/conceptosDeInfracciones';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';
import { toMoment } from 'utils/normalizers';
import { getRazonesInfracciones } from 'api/catalogos/razonDeCancelacionInfracciones';

const ModalCancelarInfraccion = ({
  visible,
  setVisible,
  callback,
  infraccionId,
  tipoDePadron,
}) => {
  const [form] = Form.useForm();
  const razon_de_rechazo = Form.useWatch('razon_de_rechazo', form);
  const [razones, setRazones] = useState([]);
  const [recibos, setRecibos] = useState(false);

  // cancelar la infraccion
  const _cancelarInfraccion = async (values) => {
    const _cancelar = await patchInfraccion(
      infraccionId, { estado_de_la_infraccion: 1, ...values },
    );
    if (_cancelar) {
      setVisible(false);
      message.info('Infracción cancelada correctamente');
      callback();
    }
  };

  // revisar si la infraccion tiene recibos
  const _getRecibos = async () => {
    const padron_content_type = getContenTypePorTipoDePadron(tipoDePadron);
    const _recibos = await getRecibos({
      content_type: padron_content_type,
      object_id_padron: infraccionId,
    });
    if (_recibos) return _recibos;
    return [];
  };

  // Revisar si la infraccion tiene conceptos ligados
  const _getConceptosLigados = async (values) => {
    try {
      const _conceptos = await getConceptosDeInfracciones({ infraccion: infraccionId });

      if (_conceptos?.length > 0) {
        const conceptos_activos = _conceptos.filter((c) => c.concepto_activo !== false)
          .map((c) => (c.id));

        if (conceptos_activos.length > 0) {
          const _cancelarConceptos = await deleteConceptoDeInfraccion({
            infraccionId,
            conceptos: conceptos_activos,
          });

          if (_cancelarConceptos) {
            message.info('Conceptos cancelados correctamente');
            await _cancelarInfraccion(values);
          }
        }
      } else {
        await _cancelarInfraccion(values);
      }
    } catch (error) {
      message.error('Error al cancelar conceptos');
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _recibos = await _getRecibos();

      if (_recibos?.count > 0) {
        const recibosNoCancelados = _recibos.results
          .filter((r) => r.fecha_de_cancelacion_o_devolucion === null);

        if (recibosNoCancelados.length > 0) {
          setVisible(false);
          message.error('No se puede cancelar la infracción, ya que tiene recibos generados');
          return;
        }
      }

      const _razones = await getRazonesInfracciones();
      if (mounted) {
        setRecibos(true);
        setRazones(_razones);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const razon = razones.find((r) => r.id === razon_de_rechazo);

  const onFinish = async () => {
    const values = form.getFieldsValue();

    values.infraccionId = infraccionId;
    values.fecha_de_cancelacion = toMoment(new Date(), DATETIME_FRONTEND_FORMAT);

    await _getConceptosLigados(values);
    const request = await patchInfraccion(infraccionId, values);

    if (!request) {
      message.error('Error al cancelar la infracción');
      return;
    }
    callback();
    setVisible(false);
    message.info('Trámite cancelado correctamente');
    form.resetFields();
  };

  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => {
        setVisible(false);
      },
    },
    {
      text: 'Cancelar',
      icon: <StopOutlined />,
      onClick: () => form.submit(),
      danger: true,
      className: 'no-color',
    },
  ];

  const rules = { required: [{ required: true }] };

  return (
    recibos && (
      <Modal
        footer={null}
        closable={false}
        visible={visible}
        title={<FormSubmitControls label="Cancelar Infraccion" controls={controls} />}
      >
        <Row>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              name="cancelar_tramite"
              onFinish={() => onFinish()}
            >
              <Col span={24}>
                <Form.Item
                  label="Comentarios"
                  name="observaciones_de_cancelacion"
                  rules={rules.required}
                >
                  <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Razón de Cancelación"
                  name="razon_de_cancelacion"
                  rules={rules.required}
                >
                  <Select dataSource={razones} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Uploader
                  title="Archivo adjunto"
                  formItemName="archivo_cancelacion"
                  rules={[{ required: !!razon?.necesita_archivo }]}
                  allowedExtensions={['pdf', 'docx']}
                  limitMB={5}
                  fullSize
                />
              </Col>
              <br />
            </Form>
          </Col>
        </Row>
      </Modal>
    )
  );
};

ModalCancelarInfraccion.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  infraccionId: PropTypes.number,
  tipoDePadron: PropTypes.string,
};

ModalCancelarInfraccion.defaultProps = {
  infraccionId: null,
  tipoDePadron: null,
};

export default ModalCancelarInfraccion;
